import { FC, useState, useEffect } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { LoaderFunctionArgs } from 'react-router-dom';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import type { RootState } from '../types';

import MainContainer from '../components/ui_components/MainContainer';
import TableListMatch from '../components/specific_ppv_year_info_component/TableListMatch';
import CreatePpvSpecificModale from '../components/admin/PpvHandleFolder/CreatePpvSpecificModale';
import { apiListArenasByPage } from '../api/arenaApi';
import colors from '../styles/colors';

interface PpvDescription {
    name: string;
}

interface Arena {
    id: number;
    name: string;
}

interface SpecificPpv {
    id: number;
    ppv_description_id: number;
    date: string;
    img_path: string;
    night1?: string;
    night2?: string;
    night3?: string;
    ppv_description: PpvDescription;
    arena?: Arena;
    fights: unknown[];
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const BoxPoster = styled.div`
    width: 220px;
    aspect-ratio: 2 / 3;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(163, 27, 27, 0.35);
    flex-shrink: 0;

    & img.poster-ppv {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top;
        display: block;
    }
`;

const BoxArena = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;

    & h5 {
        color: rgba(255, 255, 255, 0.55);
        font-size: 14px;
        margin: 0;

        &:hover {
            cursor: pointer;
            color: rgba(255, 255, 255, 0.9);
        }
    }
`;

const BoxListMatch = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 90%;
    margin-top: 10px;
`;

const BoxLinkNightsPpv = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-bottom: 10px;
    row-gap: 5px;
`;

const ButtonNightPpv = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 6px 20px;
    border: 1px solid rgba(163, 27, 27, 0.5);
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    color: rgba(255, 255, 255, 0.8);
    background-color: rgba(163, 27, 27, 0.1);
    min-width: 150px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.5px;

    &:hover {
        background-color: rgba(163, 27, 27, 0.3);
        border-color: rgba(163, 27, 27, 0.8);
        color: #fff;
    }
`;

const NoNightText = styled.div`
    color: rgba(255, 255, 255, 0.4);
    font-size: 14px;
    font-style: italic;
    padding: 6px 14px;
    border: 1px dashed rgba(255, 255, 255, 0.15);
    border-radius: 5px;
`;


const SpecificPpvYearInfo: FC = () => {
    const { user } = useSelector((state: RootState) => state.auth);
    const specificPpv = useLoaderData() as SpecificPpv;

    const navigate = useNavigate();

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [arenasList, setArenasList] = useState<any[]>([]);

    const navigateGoToArenaPage = (): void => {
        const url = '/arenas/' + specificPpv.arena?.id;
        navigate(url);
    };

    const navigateGoToNightPpv = (nightUrl: string): void => {
        window.open(nightUrl, '_blank');
    };

    const fetchArenas = async () => {
        try {
            const response = await apiListArenasByPage('all') as any;
            if (!response.error && response.data && response.data.data) {
                setArenasList(response.data.data);
            }
        } catch (error) {
            console.error('Errore nel caricamento arene:', error);
        }
    };

    const handleOpenEditModal = (): void => {
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = (): void => {
        setIsEditModalOpen(false);
    };

    const handlePpvEdited = (): void => {
        window.location.reload();
    };

    // Converte DD-MM-YYYY in YYYY-MM-DD per il date picker del modal
    const dateParts = specificPpv.date ? specificPpv.date.split('-') : [];
    const formattedDate = dateParts.length === 3 ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}` : specificPpv.date;

    const editData = {
        id: specificPpv.id, 
        date: formattedDate,
        img_path: specificPpv.img_path,
        arena_id: specificPpv.arena?.id || null,
        night1: specificPpv.night1 || '',
        night2: specificPpv.night2 || '',
        night3: specificPpv.night3 || '',
    };

    return (
        <MainContainer>

            <Container>
                <h1 className="m-0">{specificPpv.ppv_description.name} {specificPpv.date.split('-')[2]}</h1>
                <p className="m-0">{specificPpv.date}</p>

                {!!(user && (user.is_admin || user.is_founder || user.is_super_admin)) && (
                    <div className="btn-edit btn btn-primary me-2 mb-2" onClick={handleOpenEditModal}>Modifica</div>
                )}

                <BoxArena className="box-arena">
                    {specificPpv.arena && (
                        <h5 onClick={() => navigateGoToArenaPage()}>{specificPpv.arena.name}</h5>
                    )}
                </BoxArena>

                <BoxLinkNightsPpv className="box-link-nights-ppv">
                    {specificPpv.night1 && (
                        <ButtonNightPpv onClick={() => navigateGoToNightPpv(specificPpv.night1!)}>Night 1</ButtonNightPpv>
                    )}
                    {specificPpv.night2 && (
                        <ButtonNightPpv onClick={() => navigateGoToNightPpv(specificPpv.night2!)}>Night 2</ButtonNightPpv>
                    )}
                    {specificPpv.night3 && (
                        <ButtonNightPpv onClick={() => navigateGoToNightPpv(specificPpv.night3!)}>Night 3</ButtonNightPpv>
                    )}

                    {(!specificPpv.night1 && !specificPpv.night2 && !specificPpv.night3) && (
                        <NoNightText>Nessun link disponibile</NoNightText>
                    )}
                </BoxLinkNightsPpv>


                <BoxPoster className="box-poster">
                    <img
                        className="poster-ppv"
                        src={process.env.REACT_APP_STORAGE_URL + specificPpv.img_path}
                        alt={specificPpv.img_path + "_alt"}
                    >
                    </img>
                </BoxPoster>


                <BoxListMatch>
                    <h2>Lista Match</h2>
                    <TableListMatch
                        listMatch={specificPpv.fights}
                        specificPpv={specificPpv}
                        user={user}
                    />
                </BoxListMatch>
            </Container>

            {isEditModalOpen && (
                <CreatePpvSpecificModale
                    ppvGeneraleId={specificPpv.ppv_description_id}
                    editData={editData}
                    arenasList={arenasList}
                    onArenasLoad={fetchArenas}
                    closeModalClick={handleCloseEditModal}
                    onPpvSpecificCreated={handlePpvEdited}
                />
            )}

        </MainContainer>
    );
};


export default SpecificPpvYearInfo;

export async function loader({ params }: LoaderFunctionArgs) {
    const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'api/ppv/specific/' + params.idString);
    if (!response.ok) {
        return null;
    } else {
        const resData = await response.json();
        return resData;
    }
}
